import React, { useState, useEffect } from 'react';
import {
    Table, Button, Modal, message, Input, Row, Col, Typography,
    DatePicker, Card, Divider, Descriptions, Tabs, Tag, Space, Statistic, Spin,
    Dropdown, Menu, Progress, Badge, Avatar
} from 'antd';
import {
    ShoppingOutlined, AppstoreOutlined, PartitionOutlined,
    LineChartOutlined, BarChartOutlined, PieChartOutlined,
    EditOutlined, HistoryOutlined,
    FilterOutlined, DownloadOutlined, MoreOutlined,
    ArrowUpOutlined, ArrowDownOutlined, StockOutlined
} from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';
import moment from 'moment';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { getPermission } from '../../constants';

const { Search } = Input;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#FF6B6B', '#4ECDC4'];

const Products = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [advancedSalesData, setAdvancedSalesData] = useState(null);
    const [advancedModalVisible, setAdvancedModalVisible] = useState(false);
    const [dateRange, setDateRange] = useState(null);
    const [advancedLoading, setAdvancedLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const userPermission = getPermission();

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/products');
            const productData = response.data.products || [];
            setProducts(productData);
            setFilteredProducts(productData);
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch products.');
        } finally {
            setLoading(false);
        }
    };

    const handleAdvancedAnalysis = async (shopId, productId) => {
        setAdvancedLoading(true);
        try {
            let url = `/sales/advanced/${shopId}/${productId}`;
            const params = new URLSearchParams();

            if (dateRange && dateRange[0]) {
                params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
            }
            if (dateRange && dateRange[1]) {
                params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
            }

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await axiosInstance.get(url);
            setAdvancedSalesData(response.data);
            setAdvancedModalVisible(true);
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch advanced sales analysis.');
        } finally {
            setAdvancedLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearchQuery(value);
        if (value) {
            const filtered = products.filter(
                (product) =>
                    product.name.toLowerCase().includes(value.toLowerCase()) ||
                    product.category?.name?.toLowerCase().includes(value.toLowerCase()) ||
                    product.sub_category?.name?.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredProducts(filtered);
        } else {
            setFilteredProducts(products);
        }
    };

    const renderCurrency = (value) => {
        return `Tsh ${parseFloat(value).toFixed(2)}`;
    };

    const renderSaleDate = (saleDate) => {
        return moment(saleDate).format('D/M/YYYY h:mm A');
    };

    const prepareChartData = (data) => {
        if (!data) return [];

        const salesByDate = {};
        data.sales_details.forEach(sale => {
            const date = moment(sale.date).format('YYYY-MM-DD');
            if (!salesByDate[date]) {
                salesByDate[date] = {
                    date,
                    sales: 0,
                    revenue: 0,
                    profit: 0,
                    quantity: 0
                };
            }
            salesByDate[date].sales += 1;
            salesByDate[date].revenue += sale.revenue;
            salesByDate[date].profit += sale.profit;
            salesByDate[date].quantity += sale.quantity;
        });

        return Object.values(salesByDate).sort((a, b) =>
            moment(a.date).valueOf() - moment(b.date).valueOf()
        );
    };

    const prepareInventoryData = (data) => {
        if (!data) return [];

        return [
            { name: 'Starting', value: data.inventory_analysis.starting_quantity },
            { name: 'Added', value: data.inventory_analysis.total_added },
            { name: 'Removed', value: data.inventory_analysis.total_removed },
            { name: 'Sold', value: data.inventory_analysis.total_sold },
            { name: 'Current', value: data.inventory_analysis.actual_remaining }
        ];
    };

    const preparePaymentData = (data) => {
        if (!data) return [];

        return [
            { name: 'Receipts', value: data.sales_summary.by_payment_type.receipts.units_sold },
            { name: 'Invoices Paid', value: data.sales_summary.by_payment_type.invoices.paid.units_sold },
            { name: 'Invoices Unpaid', value: data.sales_summary.by_payment_type.invoices.unpaid.units_sold }
        ];
    };

    const getCategoryColor = (category) => {
        const colors = ['#f5222d', '#fa8c16', '#52c41a', '#1890ff', '#722ed1', '#13c2c2', '#eb2f96'];
        const index = category ? category.charCodeAt(0) % colors.length : 0;
        return colors[index];
    };

    const columns = [
        {
            title: 'Product',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                        style={{
                            backgroundColor: getCategoryColor(record.category?.name),
                            marginRight: 12,
                            color: '#fff'
                        }}
                    >
                        {text.charAt(0).toUpperCase()}
                    </Avatar>
                    <div>
                        <div style={{ fontWeight: 500 }}>{text}</div>
                        {/* <div style={{ fontSize: 12, color: '#888' }}>SKU: {record.sku || 'N/A'}</div> */}
                    </div>
                </div>
            ),
        },
        {
            title: 'Category',
            dataIndex: ['category', 'name'],
            key: 'category',
            render: (text) => (
                <Tag color={getCategoryColor(text)}>
                    {text || 'Uncategorized'}
                </Tag>
            ),
        },

        {
            title: 'Action',
            key: 'action',
            render: (_, product) => (
                <Dropdown
                    overlay={
                        <Menu>
                            <Menu.Item
                                key="analysis"
                                icon={<LineChartOutlined />}
                                onClick={() => handleAdvancedAnalysis(product.shop_id, product.id)}
                            >
                                Advanced Analysis
                            </Menu.Item>
                            {/* <Menu.Item key="edit" icon={<EditOutlined />}>
                                Edit Product
                            </Menu.Item>
                            <Menu.Item key="history" icon={<HistoryOutlined />}>
                                View History
                            </Menu.Item> */}
                        </Menu>
                    }
                    trigger={['click']}
                >
                    <Button type="text" icon={<MoreOutlined />} />
                </Dropdown>
            ),
        },
    ];

    const renderAdvancedAnalysisModal = () => {
        if (!advancedSalesData) return null;

        const chartData = prepareChartData(advancedSalesData);
        const inventoryData = prepareInventoryData(advancedSalesData);
        const paymentData = preparePaymentData(advancedSalesData);

        const adjustmentColumns = [
            {
                title: 'Date',
                dataIndex: 'date',
                key: 'date',
                render: (text) => moment(text).format('DD/MM/YYYY HH:mm'),
            },
            {
                title: 'Type',
                dataIndex: 'type',
                key: 'type',
                render: (text) => (
                    <Tag color={text === 'positive' ? 'green' : 'red'}>
                        {text === 'positive' ? 'Addition' : 'Removal'}
                    </Tag>
                ),
            },
            {
                title: 'Batch',
                dataIndex: 'batch',
                key: 'batch',
                render: (text) => text || '-',
            },
            {
                title: 'Adjusted Quantity',
                dataIndex: 'quantity',
                key: 'quantity',
                align: 'right',
            },
            {
                title: 'Previous Qty',
                dataIndex: 'previous_quantity',
                key: 'previous_quantity',
                align: 'right',
            },
            {
                title: 'Adjusted By',
                dataIndex: 'adjusted_by',
                key: 'adjusted_by',
            },
            {
                title: 'Adjustment #',
                dataIndex: 'adjustment_number',
                key: 'adjustment_number',
            },
        ];

        const inventoryColumns = [
            {
                title: 'Batch',
                dataIndex: 'batch',
                key: 'batch',
                render: (text) => text || '-',
            },
            {
                title: 'Current Quantity',
                dataIndex: 'current_quantity',
                key: 'current_quantity',
                align: 'right',
            },
            {
                title: 'Buying Price',
                dataIndex: 'buying_price',
                key: 'buying_price',
                render: renderCurrency,
                align: 'right',
            },
            {
                title: 'Selling Price',
                dataIndex: 'selling_price',
                key: 'selling_price',
                render: renderCurrency,
                align: 'right',
            },
            {
                title: 'Expiry Date',
                dataIndex: 'expiry_date',
                key: 'expiry_date',
                render: (text) => text ? moment(text).format('DD/MM/YYYY') : 'N/A',
            },
        ];

        const salesColumns = [
            {
                title: 'Date',
                dataIndex: 'date',
                key: 'date',
                render: renderSaleDate,
            },
            {
                title: 'Type',
                dataIndex: 'type',
                key: 'type',
                render: (text) => text.charAt(0).toUpperCase() + text.slice(1),
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (text) => (
                    <Tag color={text === 'paid' ? 'green' : 'orange'}>
                        {text}
                    </Tag>
                ),
            },
            {
                title: 'Batch',
                dataIndex: 'batch',
                key: 'batch',
                render: (text) => text || '-',
            },
            {
                title: 'Quantity',
                dataIndex: 'quantity',
                key: 'quantity',
                align: 'right',
            },
            {
                title: 'Revenue',
                dataIndex: 'revenue',
                key: 'revenue',
                render: renderCurrency,
                align: 'right',
            },
            userPermission?.is_view_profit && { // 👈 Only include this column if permitted
                title: 'Profit',
                dataIndex: 'profit',
                key: 'profit',
                render: (text) => (
                    <Text type={text >= 0 ? 'success' : 'danger'}>
                        {renderCurrency(text)}
                    </Text>
                ),
                align: 'right',
            },
            {
                title: 'Customer',
                dataIndex: 'customer_name',
                key: 'customer_name',
            },
        ].filter(Boolean); // 👈 Removes false entries


        return (
            <Modal
                title={
                    <div>
                        <StockOutlined style={{ marginRight: 8 }} />
                        {`Advanced Sales Analysis: ${advancedSalesData.product_info.name}`}
                    </div>
                }
                visible={advancedModalVisible}
                onCancel={() => setAdvancedModalVisible(false)}
                footer={null}
                width="90%"
                style={{ top: 20 }}
                destroyOnClose={true}
                confirmLoading={advancedLoading}
            >
                <Spin spinning={advancedLoading}>
                    <Tabs defaultActiveKey="1">
                        <TabPane
                            tab={
                                <span>
                                    <AppstoreOutlined />
                                    Overview
                                </span>
                            }
                            key="1"
                        >
                            <Row gutter={16}>
                                <Col span={6}>
                                    <Card bordered={false}>
                                        <Statistic
                                            title="Starting Quantity"
                                            value={advancedSalesData.inventory_analysis.starting_quantity}
                                            prefix={<ArrowUpOutlined />}
                                            valueStyle={{ color: '#1890ff' }}
                                            suffix="units"
                                        />
                                    </Card>
                                </Col>
                                <Col span={6}>
                                    <Card bordered={false}>
                                        <Statistic
                                            title="Total Added"
                                            value={advancedSalesData.inventory_analysis.total_added}
                                            prefix={<ArrowUpOutlined />}
                                            valueStyle={{ color: '#52c41a' }}
                                            suffix="units"
                                        />
                                    </Card>
                                </Col>
                                <Col span={6}>
                                    <Card bordered={false}>
                                        <Statistic
                                            title="Total Removed"
                                            value={advancedSalesData.inventory_analysis.total_removed}
                                            prefix={<ArrowDownOutlined />}
                                            valueStyle={{ color: '#f5222d' }}
                                            suffix="units"
                                        />
                                    </Card>
                                </Col>
                                <Col span={6}>
                                    <Card bordered={false}>
                                        <Statistic
                                            title="Current Stock"
                                            value={advancedSalesData.inventory_analysis.actual_remaining}
                                            prefix={<ShoppingOutlined />}
                                            valueStyle={{ color: '#722ed1' }}
                                            suffix="units"
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            <Divider />

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Card
                                        title="Inventory Status"
                                        extra={
                                            <Tag color={
                                                advancedSalesData.inventory_analysis.discrepancy === 0 ?
                                                    'success' : 'error'
                                            }>
                                                {advancedSalesData.inventory_analysis.discrepancy === 0 ?
                                                    'Balanced' : 'Discrepancy Detected'}
                                            </Tag>
                                        }
                                    >
                                        <Descriptions bordered column={1}>
                                            <Descriptions.Item label="Expected Remaining">
                                                {advancedSalesData.inventory_analysis.expected_remaining} units
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Actual Remaining">
                                                {advancedSalesData.inventory_analysis.actual_remaining} units
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Discrepancy">
                                                <Text
                                                    type={advancedSalesData.inventory_analysis.discrepancy === 0 ?
                                                        'success' : 'danger'}
                                                >
                                                    {advancedSalesData.inventory_analysis.discrepancy} units
                                                    {advancedSalesData.inventory_analysis.discrepancy !== 0 && (
                                                        <Text type="warning" style={{ marginLeft: 8 }}>
                                                            (Inventory mismatch detected)
                                                        </Text>
                                                    )}
                                                </Text>
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </Card>
                                </Col>
                                <Col span={12}>
                                    <Card
                                        title="Sales Summary"
                                    // extra={
                                    //     <Tag color="#52c41a">
                                    //         Last Updated: {moment().format('D/M/YYYY')}
                                    //     </Tag>
                                    // }
                                    >
                                        <Descriptions bordered column={1}>
                                            <Descriptions.Item label="Units Sold">
                                                {advancedSalesData.sales_summary.total_units_sold} units
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Total Revenue">
                                                {renderCurrency(advancedSalesData.sales_summary.total_revenue)}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Total Cost">
                                                {renderCurrency(advancedSalesData.sales_summary.total_cost)}
                                            </Descriptions.Item>
                                            {userPermission?.is_view_profit && <Descriptions.Item label="Net Profit/Loss">
                                                <Text
                                                    type={
                                                        (advancedSalesData.sales_summary.total_profit -
                                                            advancedSalesData.sales_summary.total_loss) >= 0 ?
                                                            'success' : 'danger'
                                                    }
                                                >
                                                    {renderCurrency(
                                                        advancedSalesData.sales_summary.total_profit -
                                                        advancedSalesData.sales_summary.total_loss
                                                    )}
                                                </Text>
                                            </Descriptions.Item>}
                                        </Descriptions>
                                    </Card>
                                </Col>
                            </Row>
                        </TabPane>

                        <TabPane
                            tab={
                                <span>
                                    <ShoppingOutlined />
                                    Inventory Details
                                </span>
                            }
                            key="2"
                        >
                            <Card
                                title="Current Inventory Items"
                                style={{ marginBottom: 24 }}
                                extra={
                                    <Button type="primary" icon={<DownloadOutlined />}>
                                        Export
                                    </Button>
                                }
                            >
                                <Table
                                    columns={inventoryColumns}
                                    dataSource={advancedSalesData.inventory_analysis.inventory_items}
                                    rowKey="id"
                                    pagination={false}
                                    scroll={{ x: 'max-content' }}
                                />
                            </Card>

                            <Card
                                title="Inventory Adjustments"
                                extra={
                                    <Button icon={<FilterOutlined />}>
                                        Filter
                                    </Button>
                                }
                            >
                                <Table
                                    columns={adjustmentColumns}
                                    dataSource={advancedSalesData.inventory_changes.adjustments_details}
                                    rowKey="adjustment_id"
                                    pagination={{ pageSize: 5 }}
                                    scroll={{ x: 'max-content' }}
                                />
                            </Card>
                        </TabPane>

                        <TabPane
                            tab={
                                <span>
                                    <BarChartOutlined />
                                    Sales Details
                                </span>
                            }
                            key="3"
                        >
                            <Card
                                title="Sales Transactions"
                                extra={
                                    <Space>
                                        <RangePicker
                                            style={{ width: '100%' }}
                                            value={dateRange}
                                            onChange={setDateRange}
                                        />
                                        <Button
                                            type="primary"
                                            onClick={() => handleAdvancedAnalysis(
                                                advancedSalesData.product_info.shop_id,
                                                advancedSalesData.product_info.id
                                            )}
                                            loading={advancedLoading}
                                        >
                                            Apply
                                        </Button>
                                    </Space>
                                }
                            >
                                <Table
                                    columns={salesColumns}
                                    dataSource={advancedSalesData.sales_details}
                                    rowKey="sale_id"
                                    pagination={{ pageSize: 5 }}
                                    scroll={{ x: 'max-content' }}
                                />
                            </Card>
                        </TabPane>

                        <TabPane
                            tab={
                                <span>
                                    <PieChartOutlined />
                                    Payment Analysis
                                </span>
                            }
                            key="4"
                        >
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Card
                                        title="Receipts (Paid Immediately)"
                                        headStyle={{ borderBottom: '2px solid #1890ff' }}
                                    >
                                        <Descriptions column={1}>
                                            <Descriptions.Item label="Units Sold">
                                                <Text strong>
                                                    {advancedSalesData.sales_summary.by_payment_type.receipts.units_sold}
                                                </Text>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Revenue">
                                                <Text strong>
                                                    {renderCurrency(advancedSalesData.sales_summary.by_payment_type.receipts.revenue)}
                                                </Text>
                                            </Descriptions.Item>
                                            {userPermission?.is_view_profit && <Descriptions.Item label="Profit">
                                                <Text
                                                    strong
                                                    type={advancedSalesData.sales_summary.by_payment_type.receipts.profit >= 0 ?
                                                        'success' : 'danger'}
                                                >
                                                    {renderCurrency(advancedSalesData.sales_summary.by_payment_type.receipts.profit)}
                                                </Text>
                                            </Descriptions.Item>}
                                        </Descriptions>
                                    </Card>
                                </Col>
                                <Col span={8}>
                                    <Card
                                        title="Invoices (Paid)"
                                        headStyle={{ borderBottom: '2px solid #52c41a' }}
                                    >
                                        <Descriptions column={1}>
                                            <Descriptions.Item label="Units Sold">
                                                <Text strong>
                                                    {advancedSalesData.sales_summary.by_payment_type.invoices.paid.units_sold}
                                                </Text>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Revenue">
                                                <Text strong>
                                                    {renderCurrency(advancedSalesData.sales_summary.by_payment_type.invoices.paid.revenue)}
                                                </Text>
                                            </Descriptions.Item>
                                            {userPermission?.is_view_profit && <Descriptions.Item label="Profit">
                                                <Text
                                                    strong
                                                    type={advancedSalesData.sales_summary.by_payment_type.invoices.paid.profit >= 0 ?
                                                        'success' : 'danger'}
                                                >
                                                    {renderCurrency(advancedSalesData.sales_summary.by_payment_type.invoices.paid.profit)}
                                                </Text>
                                            </Descriptions.Item>}
                                        </Descriptions>
                                    </Card>
                                </Col>
                                <Col span={8}>
                                    <Card
                                        title="Invoices (Unpaid)"
                                        headStyle={{ borderBottom: '2px solid #f5222d' }}
                                    >
                                        <Descriptions column={1}>
                                            <Descriptions.Item label="Units Sold">
                                                <Text strong>
                                                    {advancedSalesData.sales_summary.by_payment_type.invoices.unpaid.units_sold}
                                                </Text>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Revenue">
                                                <Text strong>
                                                    {renderCurrency(advancedSalesData.sales_summary.by_payment_type.invoices.unpaid.revenue)}
                                                </Text>
                                            </Descriptions.Item>
                                            {userPermission?.is_view_profit && <Descriptions.Item label="Profit">
                                                <Text
                                                    strong
                                                    type={advancedSalesData.sales_summary.by_payment_type.invoices.unpaid.profit >= 0 ?
                                                        'success' : 'danger'}
                                                >
                                                    {renderCurrency(advancedSalesData.sales_summary.by_payment_type.invoices.unpaid.profit)}
                                                </Text>
                                            </Descriptions.Item>}
                                        </Descriptions>
                                    </Card>
                                </Col>
                            </Row>
                        </TabPane>

                        <TabPane
                            tab={
                                <span>
                                    <LineChartOutlined />
                                    Visualizations
                                </span>
                            }
                            key="5"
                        >
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <Card
                                        title="Sales Trend Over Time"
                                        extra={
                                            <Space>
                                                <Button icon={<DownloadOutlined />}>Export</Button>
                                                <Button icon={<FilterOutlined />}>Filter</Button>
                                            </Space>
                                        }
                                    >
                                        <ResponsiveContainer width="100%" height={400}>
                                            <AreaChart data={chartData}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                <XAxis
                                                    dataKey="date"
                                                    tick={{ fill: '#666' }}
                                                    axisLine={{ stroke: '#d9d9d9' }}
                                                />
                                                <YAxis
                                                    yAxisId="left"
                                                    orientation="left"
                                                    tick={{ fill: '#666' }}
                                                    axisLine={{ stroke: '#d9d9d9' }}
                                                />
                                                <YAxis
                                                    yAxisId="right"
                                                    orientation="right"
                                                    tick={{ fill: '#666' }}
                                                    axisLine={{ stroke: '#d9d9d9' }}
                                                />
                                                {userPermission?.is_view_profit && <Tooltip
                                                    formatter={(value, name) => {
                                                        if (name === 'Revenue' || name === 'Profit') {
                                                            return renderCurrency(value);
                                                        }
                                                        return value;
                                                    }}
                                                />}
                                                <Legend />
                                                <Area
                                                    yAxisId="left"
                                                    type="monotone"
                                                    dataKey="quantity"
                                                    name="Quantity Sold"
                                                    stroke="#8884d8"
                                                    fill="#8884d8"
                                                    fillOpacity={0.2}
                                                />
                                                <Area
                                                    yAxisId="right"
                                                    type="monotone"
                                                    dataKey="revenue"
                                                    name="Revenue (Tsh)"
                                                    stroke="#82ca9d"
                                                    fill="#82ca9d"
                                                    fillOpacity={0.2}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </Card>
                                </Col>
                            </Row>

                            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                                <Col span={12}>
                                    <Card title="Inventory Movements">
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart data={inventoryData}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                <XAxis
                                                    dataKey="name"
                                                    tick={{ fill: '#666' }}
                                                    axisLine={{ stroke: '#d9d9d9' }}
                                                />
                                                <YAxis
                                                    tick={{ fill: '#666' }}
                                                    axisLine={{ stroke: '#d9d9d9' }}
                                                />
                                                <Tooltip
                                                    formatter={(value) => `${value} units`}
                                                />
                                                <Legend />
                                                <Bar
                                                    dataKey="value"
                                                    name="Quantity"
                                                    fill="#8884d8"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Card>
                                </Col>
                                <Col span={12}>
                                    <Card title="Sales by Payment Type">
                                        <ResponsiveContainer width="100%" height={300}>
                                            <PieChart>
                                                <Pie
                                                    data={paymentData}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    outerRadius={80}
                                                    fill="#8884d8"
                                                    dataKey="value"
                                                    nameKey="name"
                                                    label={({ name, percent }) =>
                                                        `${name}: ${(percent * 100).toFixed(0)}%`
                                                    }
                                                >
                                                    {paymentData.map((entry, index) => (
                                                        <Cell
                                                            key={`cell-${index}`}
                                                            fill={COLORS[index % COLORS.length]}
                                                        />
                                                    ))}
                                                </Pie>
                                                <Tooltip
                                                    formatter={(value) => `${value} units`}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Card>
                                </Col>
                            </Row>

                            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                                {userPermission?.is_view_profit && <Col span={24}>
                                    <Card title="Daily Profit Analysis">
                                        <ResponsiveContainer width="100%" height={400}>
                                            <BarChart data={chartData}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                <XAxis
                                                    dataKey="date"
                                                    tick={{ fill: '#666' }}
                                                    axisLine={{ stroke: '#d9d9d9' }}
                                                />
                                                <YAxis
                                                    tick={{ fill: '#666' }}
                                                    axisLine={{ stroke: '#d9d9d9' }}
                                                />
                                                <Tooltip
                                                    formatter={(value) => renderCurrency(value)}
                                                />
                                                <Legend />
                                                <Bar
                                                    dataKey="profit"
                                                    name="Daily Profit (Tsh)"
                                                    fill="#82ca9d"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Card>
                                </Col>}
                            </Row>
                        </TabPane>
                    </Tabs>
                </Spin>
            </Modal>
        );
    };

    const tabItems = [
        { key: 'all', label: 'All Products' },
        // { key: 'active', label: 'Active' },
        // { key: 'low-stock', label: 'Low Stock' },
        // { key: 'out-of-stock', label: 'Out of Stock' },
    ];

    return (
        <div style={{ padding: '24px', maxWidth: '100%', background: '#f5f7fa' }}>
            <Card
                bordered={false}
                style={{ marginBottom: 24, borderRadius: 8 }}
                bodyStyle={{ paddingBottom: 0 }}
            >
                <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
                    <Col>
                        <Title level={3} style={{ margin: 0 }}>
                            <ShoppingOutlined style={{ marginRight: 12, color: '#1890ff' }} />
                            Product Inventory
                        </Title>
                    </Col>
                    <Col>
                        <Space>
                            <Button type="primary" icon={<DownloadOutlined />}>
                                Export
                            </Button>
                            <Button icon={<FilterOutlined />}>
                                Filters
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={8}>
                        <Card bordered={false} style={{ borderRadius: 8 }}>
                            <Statistic
                                title="Total Products"
                                value={products.length}
                                prefix={<ShoppingOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card bordered={false} style={{ borderRadius: 8 }}>
                            <Statistic
                                title="Categories"
                                value={new Set(products.map(p => p.category?.name)).size}
                                prefix={<AppstoreOutlined />}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card bordered={false} style={{ borderRadius: 8 }}>
                            <Statistic
                                title="Subcategories"
                                value={new Set(products.map(p => p.sub_category?.name)).size}
                                prefix={<PartitionOutlined />}
                                valueStyle={{ color: '#722ed1' }}
                            />
                        </Card>
                    </Col>
                </Row>

                <Row justify="space-between" style={{ marginBottom: 20 }}>
                    <Col>
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            style={{ marginBottom: -17 }}
                        >
                            {tabItems.map(item => (
                                <TabPane tab={item.label} key={item.key} />
                            ))}
                        </Tabs>
                    </Col>
                    <Col>
                        <Search
                            placeholder="Search products, categories..."
                            onSearch={handleSearch}
                            enterButton
                            allowClear
                            value={searchQuery}
                            onChange={(e) => handleSearch(e.target.value)}
                            style={{ width: 300 }}
                        />
                    </Col>
                </Row>
            </Card>

            <Card
                bordered={false}
                style={{ borderRadius: 8 }}
                bodyStyle={{ padding: 0 }}
            >
                <Table
                    columns={columns}
                    dataSource={filteredProducts}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} products`
                    }}
                    scroll={{ x: 'max-content' }}
                />
            </Card>

            {renderAdvancedAnalysisModal()}
        </div>
    );
};

export default Products;